import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {commitPath} from '@github-ui/paths'
import {Link, RelativeTime} from '@primer/react'
import type {ReactNode} from 'react'

import {useLayoutRouteData} from '../../../../page-data/loaders/use-layout-route-data'

/**
 * Renders a sentence describing who merged a pull request, the merge commit, and when.
 * Any content passed as `trailing` is placed inside the same inline-flex container so
 * additional sentences (e.g. a follow-up "Delete the branch to tidy up.") stay visually
 * aligned with the merger avatar.
 */
export function MergedDescription({trailing}: {trailing?: ReactNode}) {
  const {pullRequest, repository} = useLayoutRouteData()
  const {mergedBy, mergedByName, mergedByAvatarUrl, mergedTime, mergeCommitSha} = pullRequest
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  const relativeMergedTime = mergedTime ? <RelativeTime datetime={mergedTime} /> : null

  const mergerLink = mergedBy ? (
    <Link
      inline
      className="Link--inTextBlock fgColor-default d-inline-flex flex-items-center gap-1"
      href={`/${mergedBy}`}
      {...getHovercardAttributesForType('user', {login: mergedBy})}
    >
      {mergedByAvatarUrl && <GitHubAvatar src={mergedByAvatarUrl} alt="" size={16} />}
      {mergedBy || mergedByName}
    </Link>
  ) : null

  const commitLink =
    mergeCommitSha && repository?.name ? (
      <Link
        inline
        className="Link--inTextBlock"
        href={commitPath({owner: repository.ownerLogin, repo: repository.name, commitish: mergeCommitSha})}
      >
        {mergeCommitSha.slice(0, 7)}
      </Link>
    ) : null

  if (!mergerLink && !commitLink) {
    return (
      <span className="d-inline-flex flex-items-center flex-wrap gap-1">
        This pull request was merged.
        {relativeMergedTime}
        <span className="ml-n1">.</span>
        {trailing}
      </span>
    )
  }

  return (
    <span className="d-inline-flex flex-items-center flex-wrap gap-1">
      <span className="d-inline-flex flex-items-center flex-wrap gap-1">
        {mergerLink ? (
          <>
            {mergerLink} merged
            {commitLink ? <> in {commitLink}</> : null}
            {relativeMergedTime ? <> {relativeMergedTime}</> : null}
            <span className="ml-n1">.</span>
          </>
        ) : (
          <>
            Merged in {commitLink}
            {relativeMergedTime ? <> {relativeMergedTime}</> : null}
            <span className="ml-n1">.</span>
          </>
        )}
      </span>
      {trailing}
    </span>
  )
}
